import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import jwtAxios, { setAuthToken } from './index';
import { useInfoViewActionsContext } from '@crema/context/AppContextProvider/InfoViewContextProvider';
import { setToken, clearToken } from './token';
import { registerAuthFailureHandler } from '../../../../api/attachAuthInterceptors';

const JWTAuthContext = createContext();
const JWTAuthActionsContext = createContext();

export const useJWTAuth = () => useContext(JWTAuthContext);

export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

const JWTAuthAuthProvider = ({ children }) => {
  const { fetchStart, fetchSuccess, fetchError } = useInfoViewActionsContext();
  const [jwtData, setJWTAuthData] = useState({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    renewTokenTimeoutId: undefined,
  });
  const timeoutTimer = 60000;

  const getAuthUser = useCallback(() => {
    fetchStart();
    const token = localStorage.getItem('token');

    if (!token) {
      fetchSuccess();
      setJWTAuthData((prev) => {
        if (prev?.renewTokenTimeoutId) {
          clearTimeout(prev.renewTokenTimeoutId);
        }
        return {
          user: undefined,
          isLoading: false,
          isAuthenticated: false,
          renewTokenTimeoutId: undefined,
        };
      });
      return;
    }

    jwtAxios
      .post('api/usuarios/refreshToken', {
        Token: token,
        FillUserData: true,
      })
      .then(({ data }) => {
        localStorage.setItem('token', data.token);
        setAuthToken(data.token);
        setToken(data.token);
        const timeoutId = window.setTimeout(() => getAuthUser(), timeoutTimer);
        setJWTAuthData((prev) => {
          if (prev?.renewTokenTimeoutId) {
            clearTimeout(prev.renewTokenTimeoutId);
          }
          return {
            user: { ...prev?.user, ...data.user },
            isAuthenticated: true,
            isLoading: false,
            renewTokenTimeoutId: timeoutId,
          };
        });
        fetchSuccess();
      })
      .catch(() => {
        localStorage.removeItem('token');
        setAuthToken();
        clearToken();
        setJWTAuthData((prev) => {
          if (prev?.renewTokenTimeoutId) {
            clearTimeout(prev.renewTokenTimeoutId);
          }
          return {
            user: undefined,
            isAuthenticated: false,
            isLoading: false,
            renewTokenTimeoutId: undefined,
          };
        });
        fetchSuccess();
      });
  }, [fetchStart, fetchSuccess, timeoutTimer]);

  useEffect(() => {
    getAuthUser();
  }, [getAuthUser]);

  const signInUser = useCallback(
    async ({ company, user, password }) => {
      fetchStart();
      try {
        const { data } = await jwtAxios.post('api/usuarios/authenticate', {
          Company: company,
          Name: user,
          Password: password,
          FillUserData: true,
        });
        localStorage.setItem('token', data.token);
        setAuthToken(data.token);
        setToken(data.token);
        const timeoutId = window.setTimeout(() => getAuthUser(), timeoutTimer);
        setJWTAuthData((prev) => {
          if (prev?.renewTokenTimeoutId) {
            clearTimeout(prev.renewTokenTimeoutId);
          }
          return {
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            renewTokenTimeoutId: timeoutId,
          };
        });
        fetchSuccess();
      } catch (error) {
        localStorage.removeItem('token');
        setAuthToken();
        clearToken();
        setJWTAuthData((prev) => {
          if (prev?.renewTokenTimeoutId) {
            clearTimeout(prev.renewTokenTimeoutId);
          }
          return {
            ...prev,
            isAuthenticated: false,
            isLoading: false,
            renewTokenTimeoutId: undefined,
          };
        });
        fetchError(error?.response?.data?.error || 'Something went wrong');
      }
    },
    [fetchStart, fetchSuccess, fetchError, getAuthUser, timeoutTimer],
  );

  const setUser = useCallback((user) => {
    setJWTAuthData((prev) => {
      if (prev?.renewTokenTimeoutId) {
        clearTimeout(prev.renewTokenTimeoutId);
      }
      return {
        ...prev,
        user: user,
        isAuthenticated: false,
        isLoading: false,
        renewTokenTimeoutId: undefined,
      };
    });
  }, []);

  // const signUpUser = async ({ name, email, password }) => {
  //   fetchStart();
  //   try {
  //     const { data } = await jwtAxios.post('users', { name, email, password });
  //     localStorage.setItem('token', data.token);
  //     setAuthToken(data.token);
  //     const res = await jwtAxios.get('/auth');
  //     setJWTAuthData({
  //       user: res.data,
  //       isAuthenticated: true,
  //       isLoading: false,
  //     });
  //     fetchSuccess();
  //   } catch (error) {
  //     setJWTAuthData({
  //       ...firebaseData,
  //       isAuthenticated: false,
  //       isLoading: false,
  //     });
  //     console.log('error:', error.response.data.error);
  //     fetchError(error?.response?.data?.error || 'Something went wrong');
  //   }
  // };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setAuthToken();
    clearToken();
    setJWTAuthData((prev) => {
      if (prev?.renewTokenTimeoutId) {
        clearTimeout(prev.renewTokenTimeoutId);
      }
      return {
        user: null,
        isLoading: false,
        isAuthenticated: false,
        renewTokenTimeoutId: undefined,
      };
    });
  }, []);

  useEffect(() => {
    registerAuthFailureHandler(logout);
    return () => registerAuthFailureHandler(null);
  }, [logout]);

  return (
    <JWTAuthContext.Provider
      value={{
        ...jwtData,
      }}
    >
      <JWTAuthActionsContext.Provider
        value={{
          //signUpUser,
          signInUser,
          logout,
          setUser,
        }}
      >
        {children}
      </JWTAuthActionsContext.Provider>
    </JWTAuthContext.Provider>
  );
};
export default JWTAuthAuthProvider;

JWTAuthAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  fetchStart: PropTypes.func,
  fetchSuccess: PropTypes.func,
  fetchError: PropTypes.func,
};
